import { cn } from '@heidi/utils';
import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import type { ApprovalRequestInput } from '../projects/components/ApprovalRequestBlock.display';
import type { ResolveInlineApproval } from '../projects/components/inline-approval/useInlineApprovalResolve';
import { useInlineApprovalResolve } from '../projects/components/inline-approval/useInlineApprovalResolve';
import { ehrNameFromSessionCreateTitle } from './ehr-session-create-approval';
import { InlineApprovalActions } from './inline-approval-actions';

interface EhrSessionCreateApprovalBlockProps {
  approval: ApprovalRequestInput;
  onResolve: ResolveInlineApproval;
  /** Leading mark. The app passes the Heidi brand icon. */
  mark?: ReactNode;
}

/**
 * Slim "Heidi wants to…" banner for creating a Heidi session from an EHR
 * appointment. Names the connected EHR when the server put it on the
 * approval title.
 */
export const EhrSessionCreateApprovalBlock = ({
  approval,
  onResolve,
  mark,
}: EhrSessionCreateApprovalBlockProps) => {
  const intl = useIntl();
  const ehrName = ehrNameFromSessionCreateTitle(approval.toolCallDisplay?.title);
  const { pendingVerdict, isBusy, submit } = useInlineApprovalResolve({
    approvalId: approval.approval_id,
    onResolve,
  });
  const title = ehrName
    ? intl.formatMessage(
        {
          defaultMessage:
            'Heidi wants to create a new session from your {ehr} appointments',
          id: '8JZ7VzBvJe',
        },
        { ehr: ehrName }
      )
    : intl.formatMessage({
        defaultMessage: 'Heidi wants to create a new session from your EHR appointments',
        id: '07MiTGDJnk',
      });

  return (
    <div
      className={cn(
        'flex w-full items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2.5',
        'duration-300 animate-in fade-in slide-in-from-bottom-1'
      )}
      data-testid={`ehr-session-create-approval-${approval.approval_id}`}
    >
      {mark}
      <p className="min-w-0 flex-1 break-words text-sm font-medium leading-snug text-text-primary [overflow-wrap:anywhere]">
        {title}
      </p>
      <div className="shrink-0">
        <InlineApprovalActions
          onApprove={() => submit('approve')}
          onDeny={() => submit('deny')}
          pendingVerdict={pendingVerdict}
          disabled={isBusy}
        />
      </div>
    </div>
  );
};
